package com.schoolconnect.app.feature.studentenrollment

import androidx.compose.ui.graphics.Color
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.datasource.network.response.StudentEnrollmentBulkResponse
import com.schoolconnect.app.feature.imports.ImportErrorRow
import com.schoolconnect.app.feature.imports.downloadValidationErrorWorkbook
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import com.schoolconnect.app.util.MAX_WORKBOOK_BYTES
import com.schoolconnect.app.feature.common.pageCountForTotal
import com.schoolconnect.app.repository.imports.BulkImportRepository
import com.schoolconnect.app.repository.user.UserRepository
import com.schoolconnect.app.designsystem.AppToastCenter

class StudentEnrollmentViewModel(
    private val schoolCode: String = "",
    private val schoolId: String = "",
    private val currentUserId: String = UserSessionStore.getUserId(),
    initialState: StudentEnrollmentUiState = StudentEnrollmentUiState()
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val _uiState = MutableStateFlow(initialState)
    private var selectedWorkbookFileName: String = ""
    private var selectedWorkbookBytes: ByteArray? = null

    val uiState: StateFlow<StudentEnrollmentUiState> = _uiState.asStateFlow()

    fun onEvent(event: StudentEnrollmentUiEvent) {
        when (event) {
            StudentEnrollmentUiEvent.BackClicked -> Unit
            StudentEnrollmentUiEvent.BackToValidationSummaryClicked -> {
                _uiState.update { state ->
                    state.copy(
                        stage = StudentEnrollmentWorkflowStage.ValidationSummary,
                        steps = validationSteps(),
                        isLoading = false
                    )
                }
            }
            StudentEnrollmentUiEvent.DownloadCurrentDataClicked -> downloadWorkbook(includeSchoolCode = true)
            StudentEnrollmentUiEvent.DownloadTemplateClicked -> downloadWorkbook(includeSchoolCode = false)
            StudentEnrollmentUiEvent.UploadFileClicked -> Unit
            StudentEnrollmentUiEvent.ValidateFileClicked -> {
                validateWorkbook()
            }
            StudentEnrollmentUiEvent.DownloadErrorDetailsClicked -> downloadErrorDetails()
            StudentEnrollmentUiEvent.DismissStructureErrors -> _uiState.update { it.copy(structureValidationErrors = emptyList()) }
            StudentEnrollmentUiEvent.FilterClicked -> Unit
            StudentEnrollmentUiEvent.CancelClicked -> {
                selectedWorkbookFileName = ""
                selectedWorkbookBytes = null
                _uiState.value = StudentEnrollmentUiState()
            }
            StudentEnrollmentUiEvent.ContinueReviewClicked -> {
                _uiState.update { state ->
                    if (state.errorRows.isNotEmpty()) {
                        state.copy(showReviewWarning = true, errorMessage = "")
                    } else if (!state.serverValidationPassed) {
                        state.copy(errorMessage = "Validate the workbook before continuing.")
                    } else {
                        state.copy(
                            stage = StudentEnrollmentWorkflowStage.ReviewAndSave,
                            steps = reviewSteps(),
                            errorMessage = ""
                        )
                    }
                }
            }
            StudentEnrollmentUiEvent.DismissReviewWarning -> _uiState.update { it.copy(showReviewWarning = false) }
            StudentEnrollmentUiEvent.ConfirmContinueReviewClicked -> {
                _uiState.update {
                    it.copy(
                        showReviewWarning = false,
                        stage = StudentEnrollmentWorkflowStage.ReviewAndSave,
                        steps = reviewSteps(),
                        errorMessage = ""
                    )
                }
            }
            StudentEnrollmentUiEvent.DismissSaveWarning -> _uiState.update { it.copy(showSaveWarning = false) }
            StudentEnrollmentUiEvent.ConfirmSaveRecordsClicked -> {
                _uiState.update { it.copy(showSaveWarning = false, errorMessage = "") }
                uploadWorkbook()
            }
            StudentEnrollmentUiEvent.SaveRecordsClicked -> {
                val state = _uiState.value
                if (!state.serverValidationCompleted) {
                    _uiState.update { it.copy(errorMessage = "Validate the workbook before saving records.") }
                } else if (state.errorRows.isNotEmpty()) {
                    _uiState.update { it.copy(showSaveWarning = true, errorMessage = "") }
                } else if (state.serverValidationPassed && state.errorMessage.isBlank()) {
                    uploadWorkbook()
                } else {
                    _uiState.update { it.copy(errorMessage = "Validate the workbook before saving records.") }
                }
            }
            is StudentEnrollmentUiEvent.WorkbookPrepared -> {
                if (event.fileBytes.size > MAX_WORKBOOK_BYTES) {
                    selectedWorkbookFileName = ""
                    selectedWorkbookBytes = null
                    _uiState.update { it.copy(errorMessage = "Workbook is too large. Maximum size is 25 MB.") }
                } else {
                    selectedWorkbookFileName = event.fileName
                    selectedWorkbookBytes = event.fileBytes
                    _uiState.update {
                        it.copy(
                            serverValidationCompleted = false,
                            serverValidationPassed = false,
                            showReviewWarning = false,
                            showSaveWarning = false
                        )
                    }
                }
            }
            is StudentEnrollmentUiEvent.WorkbookLoadStarted -> {
                _uiState.update { state ->
                    state.copy(
                        isLoading = true,
                        errorMessage = "",
                        structureValidationErrors = emptyList(),
                        reviewSummary = state.reviewSummary.copy(
                            fileName = event.fileName,
                            uploadedOn = "Reading file...",
                            readyToSaveRows = 0,
                            status = "Validating"
                        )
                    )
                }
            }
            is StudentEnrollmentUiEvent.WorkbookLoaded -> applyWorkbookPreview(event.preview)
            is StudentEnrollmentUiEvent.WorkbookLoadFailed -> {
                selectedWorkbookFileName = ""
                selectedWorkbookBytes = null
                _uiState.update { state ->
                    state.copy(
                        isLoading = false,
                        errorMessage = event.message
                    )
                }
            }
            is StudentEnrollmentUiEvent.TabSelected -> _uiState.update { state ->
                state.copy(
                    selectedTabId = event.tabId,
                    reviewTabs = state.reviewTabs.map { tab ->
                        tab.copy(isSelected = tab.id.equals(event.tabId, ignoreCase = true))
                    }
                )
            }
            is StudentEnrollmentUiEvent.RowsPerPageChanged -> _uiState.update { state ->
                state.copy(rowsPerPage = event.rowsPerPage.coerceAtLeast(1))
            }
            is StudentEnrollmentUiEvent.SheetSelected -> _uiState.update { state ->
                state.copy(
                    selectedSheet = event.sheetName,
                    pageIndex = 0
                )
            }
            is StudentEnrollmentUiEvent.PageChanged -> _uiState.update { state ->
                when (state.stage) {
                    StudentEnrollmentWorkflowStage.ValidationSummary -> state.copy(pageIndex = event.pageIndex.coerceAtLeast(0))
                    StudentEnrollmentWorkflowStage.ReviewAndSave -> {
                        val tabId = state.selectedTabId
                        val updatedPageIndexes = state.reviewPageIndexByTab.toMutableMap()
                        updatedPageIndexes[tabId] = event.pageIndex.coerceAtLeast(0)
                        state.copy(
                            reviewPageIndexByTab = updatedPageIndexes
                        )
                    }
                }
            }
        }
    }

    fun clear() {
        scope.cancel()
    }

    private fun downloadWorkbook(includeSchoolCode: Boolean) {
        if (_uiState.value.isLoading) return
        _uiState.update { it.copy(isLoading = true, errorMessage = "") }
        scope.launch {
            delay(300)
            val result = if (includeSchoolCode) {
                resolveSchoolCode().fold(
                    onSuccess = { resolvedCode -> BulkImportRepository.downloadStudentEnrollmentTemplate(resolvedCode) },
                    onFailure = { throwable -> Result.failure(throwable) }
                )
            } else {
                BulkImportRepository.downloadStudentEnrollmentTemplate()
            }
            result.fold(
                onSuccess = {
                    _uiState.update { it.copy(isLoading = false) }
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Unable to download the student enrollment workbook."
                        )
                    }
                }
            )
        }
    }

    private fun validateWorkbook() {
        val state = _uiState.value
        val bytes = selectedWorkbookBytes
        if (bytes == null || bytes.isEmpty()) {
            _uiState.update { it.copy(errorMessage = "Upload an XLSX workbook before validating.") }
            return
        }
        if (state.isLoading) return
        val fileName = selectedWorkbookFileName.ifBlank { state.reviewSummary.fileName }
        _uiState.update { current ->
            current.copy(
                isLoading = true,
                serverValidationCompleted = false,
                serverValidationPassed = false,
                errorMessage = "",
                validationLastUpdated = "Validating with server..."
            )
        }
        scope.launch {
            val result = resolveSchoolCode().fold(
                onSuccess = { resolvedCode ->
                    BulkImportRepository.validateStudentEnrollment(resolvedCode, fileName, bytes, currentUserId)
                },
                onFailure = { Result.failure(it) }
            )
            result.fold(
                onSuccess = { response -> applyServerValidation(response.validationErrors.orEmpty(), response.message.orEmpty()) },
                onFailure = { throwable ->
                    _uiState.update { current ->
                        current.copy(
                            isLoading = false,
                            serverValidationCompleted = false,
                            serverValidationPassed = false,
                            errorMessage = throwable.message ?: "Failed to validate student enrollment workbook.",
                            validationLastUpdated = "Validation failed",
                            validationDetails = current.validationDetails.map { row ->
                                if (row.label == "Status") row.copy(badgeText = "Validation Failed", badgeColor = Color(0xFFDC2626)) else row
                            }
                        )
                    }
                }
            )
        }
    }

    private fun applyServerValidation(validationErrors: List<String>, message: String) {
        val serverRows = validationErrors.filter(String::isNotBlank).mapIndexed { index, error ->
            StudentEnrollmentErrorRow("Server/API", "—", error, id = "server-api:$index")
        }
        val state = _uiState.value
        val totalRows = state.studentRecords.size + state.parentRecords.size
        val errorCount = serverRows.size
        val pageCount = pageCountForTotal(errorCount, state.rowsPerPage)
        val passed = serverRows.isEmpty()
        _uiState.update { current ->
            current.copy(
                stage = if (passed) StudentEnrollmentWorkflowStage.ReviewAndSave else StudentEnrollmentWorkflowStage.ValidationSummary,
                steps = if (passed) reviewSteps() else validationSteps(),
                isLoading = false,
                serverValidationCompleted = true,
                serverValidationPassed = passed,
                errorMessage = "",
                errorRows = serverRows,
                sheetOptions = listOf("All Sheets", "Students", "Parents", "Reference Values", "Server/API"),
                selectedSheet = "All Sheets",
                totalErrorsLabel = if (passed) "Validation Passed" else "Validation Issues ($errorCount)",
                errorHelpText = if (passed) "Server validation passed. Review the records before saving." else "The server found issues in this workbook. Resolve them and validate again.",
                errorPageText = buildErrorPageText(errorCount, pageCount),
                pageIndex = 0,
                pageCount = pageCount,
                validationLastUpdated = message.ifBlank { "Server validation complete" },
                validationDetails = listOf(
                    StudentEnrollmentSummaryRow("Total Rows", totalRows.toString()),
                    StudentEnrollmentSummaryRow("Valid Rows", if (passed) totalRows.toString() else "—", valueColor = Color(0xFF16A34A)),
                    StudentEnrollmentSummaryRow("Invalid Rows", errorCount.toString(), valueColor = Color(0xFFDC2626)),
                    StudentEnrollmentSummaryRow(
                        "Status",
                        badgeText = if (passed) "Validated" else "Errors Found",
                        badgeBackground = if (passed) Color(0xFFE8F8EF) else Color(0xFFFFECEF),
                        badgeColor = if (passed) Color(0xFF16A34A) else Color(0xFFDC2626)
                    )
                ),
                reviewSummary = current.reviewSummary.copy(
                    readyToSaveRows = if (passed) totalRows else 0,
                    status = if (passed) "Ready to Save" else "Review Required"
                )
            )
        }
    }

    private fun downloadErrorDetails() {
        val state = _uiState.value
        val rows = state.errorRows.filter { row ->
            state.selectedSheet.equals("All Sheets", ignoreCase = true) ||
                row.sheetName.equals(state.selectedSheet, ignoreCase = true)
        }.map { row ->
            ImportErrorRow(sheetName = row.sheetName, rowNo = row.rowNo, message = row.message)
        }
        if (rows.isEmpty()) {
            _uiState.update { it.copy(errorMessage = "There are no validation errors to download.") }
            return
        }
        if (state.isLoading) return
        _uiState.update { it.copy(isLoading = true, errorMessage = "") }
        scope.launch {
            downloadValidationErrorWorkbook("Student_Enrollment_Error_Details.xlsx", rows).fold(
                onSuccess = { _uiState.update { it.copy(isLoading = false) } },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Unable to download validation error details."
                        )
                    }
                }
            )
        }
    }

    private fun uploadWorkbook() {
        val fileBytes = selectedWorkbookBytes
        if (fileBytes == null || fileBytes.isEmpty()) {
            _uiState.update { it.copy(errorMessage = "Upload an XLSX workbook before saving records.") }
            return
        }
        val fileName = selectedWorkbookFileName.ifBlank { _uiState.value.reviewSummary.fileName }
        _uiState.update { state ->
            state.copy(
                isLoading = true,
                errorMessage = "",
                reviewSummary = state.reviewSummary.copy(status = "Uploading...")
            )
        }
        scope.launch {
            val result = resolveSchoolCode().fold(
                onSuccess = { resolvedCode ->
                    BulkImportRepository.uploadStudentEnrollment(
                        schoolCode = resolvedCode,
                        fileName = fileName,
                        bytes = fileBytes,
                        currentUserId = currentUserId
                    )
                },
                onFailure = { throwable -> Result.failure(throwable) }
            )
            result.fold(
                onSuccess = { response -> handleUploadSuccess(response) },
                onFailure = { throwable ->
                    _uiState.update { state ->
                        state.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Failed to upload student enrollment workbook.",
                            reviewSummary = state.reviewSummary.copy(status = "Upload Failed")
                        )
                    }
                }
            )
        }
    }

    private suspend fun resolveSchoolCode(): Result<String> {
        val directCode = schoolCode.trim()
            .ifBlank { UserSessionStore.getHomeRoleSchoolCode().trim() }
        if (directCode.isNotBlank()) return Result.success(directCode)

        val targetSchoolId = schoolId.trim()
            .ifBlank { UserSessionStore.getHomeRoleSchoolId().trim() }
        return UserRepository.loadUserRoleAuthorizations(currentUserId).mapCatching { response ->
            val authorization = response.authorizations.orEmpty().firstOrNull { item ->
                targetSchoolId.isBlank() || item.tenant?.schoolId.orEmpty().trim() == targetSchoolId
            } ?: throw IllegalStateException("Unable to resolve the active school authorization.")
            authorization.tenant?.schoolCode.orEmpty().trim().takeIf { it.isNotBlank() }
                ?: throw IllegalStateException("The active school authorization has no school code.")
        }
    }

    private fun handleUploadSuccess(response: StudentEnrollmentBulkResponse) {
        val studentsCreated = response.studentsCreated ?: 0
        val studentsFailed = response.studentsFailed ?: 0
        val parentsCreated = response.parentsCreated ?: 0
        val parentsFailed = response.parentsFailed ?: 0
        val enrollmentsCreated = response.enrollmentsCreated ?: 0
        val enrollmentsFailed = response.enrollmentsFailed ?: 0
        val uploadedRows = listOf(studentsCreated, parentsCreated, enrollmentsCreated).sum()
        val failedRows = listOf(studentsFailed, parentsFailed, enrollmentsFailed).sum()
        val isSuccess = response.success == true
        val responseMessage = response.message.orEmpty()
        val summaryStatus = responseMessage.ifBlank {
            if (isSuccess) "Upload Complete" else "Upload Failed"
        }
        if (isSuccess) {
            AppToastCenter.showSuccess("Students and parents imported successfully")
            selectedWorkbookFileName = ""
            selectedWorkbookBytes = null
            _uiState.value = StudentEnrollmentUiState()
            return
        }
        _uiState.update { state ->
            state.copy(
                isLoading = false,
                errorMessage = responseMessage.ifBlank { "Student enrollment upload failed." },
                reviewSummary = state.reviewSummary.copy(
                    readyToSaveRows = uploadedRows,
                    status = summaryStatus
                ),
                validationDetails = listOf(
                    StudentEnrollmentSummaryRow("Total Rows", (uploadedRows + failedRows).toString()),
                    StudentEnrollmentSummaryRow("Valid Rows", uploadedRows.toString(), valueColor = Color(0xFF16A34A)),
                    StudentEnrollmentSummaryRow("Invalid Rows", failedRows.toString(), valueColor = Color(0xFFDC2626)),
                    StudentEnrollmentSummaryRow(
                        "Status",
                        badgeText = summaryStatus,
                        badgeBackground = if (isSuccess) Color(0xFFE8F8EF) else Color(0xFFFFECEF),
                        badgeColor = if (isSuccess) Color(0xFF16A34A) else Color(0xFFDC2626)
                    )
                ),
                validationLastUpdated = "Just now"
            )
        }
    }

    private fun applyWorkbookPreview(preview: StudentEnrollmentWorkbookPreview) {
        if (preview.structureErrors.isNotEmpty()) {
            selectedWorkbookFileName = ""
            selectedWorkbookBytes = null
            _uiState.update { state ->
                state.copy(
                    isLoading = false,
                    structureValidationErrors = preview.structureErrors,
                    errorRows = emptyList(),
                    serverValidationCompleted = false,
                    serverValidationPassed = false,
                    studentRecords = emptyList(),
                    parentRecords = emptyList(),
                    totalErrorsLabel = "Workbook Structure Error",
                    errorHelpText = "Download and fill in the Student Enrollment template, keeping its tabs and headers unchanged.",
                    errorMessage = ""
                )
            }
            return
        }
        val totalRows = preview.studentRecords.size + preview.parentRecords.size
        val invalidRows = 0
        val validRows = 0
        val reviewTabs = listOf(
            StudentEnrollmentTabUiModel(
                id = "students",
                title = "Students",
                count = preview.studentRecords.size,
                isSelected = true
            ),
            StudentEnrollmentTabUiModel(
                id = "parents",
                title = "Parents",
                count = preview.parentRecords.size
            )
        )
        val errorCount = preview.errorRows.size
        val errorPageCount = pageCountForTotal(errorCount, _uiState.value.rowsPerPage)
        val statusLabel = "Ready to Validate"
        val statusBackground = Color(0xFFFFF4D6)
        val statusColor = Color(0xFFB45309)

        _uiState.update { state ->
            state.copy(
                stage = StudentEnrollmentWorkflowStage.ValidationSummary,
                steps = validationSteps(),
                existingDetails = listOf(
                    StudentEnrollmentSummaryRow("Students", preview.studentRecords.size.toString()),
                    StudentEnrollmentSummaryRow("Parents", preview.parentRecords.size.toString())
                ),
                newImportDetails = listOf(
                    StudentEnrollmentSummaryRow("File Name", preview.fileName),
                    StudentEnrollmentSummaryRow("Selected On", preview.uploadedOn),
                    StudentEnrollmentSummaryRow("Selected By", "School Admin"),
                    StudentEnrollmentSummaryRow("Total Rows", totalRows.toString())
                ),
                validationDetails = listOf(
                    StudentEnrollmentSummaryRow("Total Rows", totalRows.toString()),
                    StudentEnrollmentSummaryRow("Valid Rows", validRows.toString(), valueColor = Color(0xFF16A34A)),
                    StudentEnrollmentSummaryRow("Invalid Rows", invalidRows.toString(), valueColor = Color(0xFFDC2626)),
                    StudentEnrollmentSummaryRow(
                        "Status",
                        badgeText = statusLabel,
                        badgeBackground = statusBackground,
                        badgeColor = statusColor
                    )
                ),
                validationLastUpdated = "Just now",
                sheetOptions = listOf("All Sheets", "Students", "Parents", "Reference Values"),
                selectedSheet = "All Sheets",
                totalErrorsLabel = "Validation Messages",
                errorHelpText = "Validate the workbook with the server to see row-level issues.",
                errorRows = emptyList(),
                serverValidationCompleted = false,
                serverValidationPassed = false,
                structureValidationErrors = emptyList(),
                errorPageText = buildErrorPageText(preview.errorRows.size, errorPageCount),
                pageIndex = 0,
                pageCount = errorPageCount,
                reviewSummary = StudentEnrollmentReviewSummary(
                    fileName = preview.fileName,
                    uploadedOn = preview.uploadedOn,
                    readyToSaveRows = validRows,
                    status = "Ready to Validate"
                ),
                reviewTabs = reviewTabs,
                selectedTabId = reviewTabs.firstOrNull()?.id.orEmpty().ifBlank { "students" },
                studentRecords = preview.studentRecords,
                parentRecords = preview.parentRecords,
                reviewPageIndexByTab = reviewTabs.associate { it.id to 0 },
                isLoading = false,
                errorMessage = preview.validationMessages.firstOrNull().orEmpty()
            )
        }
    }

    private fun validationSteps(): List<StudentEnrollmentStepUiModel> = listOf(
        StudentEnrollmentStepUiModel(number = 1, title = "Step 1", subtitle = "Upload & Validate", isActive = true),
        StudentEnrollmentStepUiModel(number = 2, title = "Step 2", subtitle = "Review & Save")
    )

    private fun reviewSteps(): List<StudentEnrollmentStepUiModel> = listOf(
        StudentEnrollmentStepUiModel(number = 1, title = "Step 1", subtitle = "Upload & Validate", isCompleted = true),
        StudentEnrollmentStepUiModel(number = 2, title = "Step 2", subtitle = "Review & Save", isActive = true)
    )

    private fun buildErrorPageText(totalErrors: Int, pageCount: Int): String {
        if (totalErrors <= 0) return "Showing 0 to 0 of 0 errors"
        val safePageCount = pageCount.coerceAtLeast(1)
        val pageSize = ((totalErrors + safePageCount - 1) / safePageCount).coerceAtLeast(1)
        val end = minOf(pageSize, totalErrors)
        return "Showing 1 to $end of $totalErrors errors"
    }
}
